/* eslint react-hooks/set-state-in-effect: ["warn"] */
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "@coworker/locales";
import { SwitchTeamSheet } from "./SwitchTeamSheet";
import { SwitchStoreSheet } from "./SwitchStoreSheet";
import { SwitchCountrySheet } from "./SwitchCountrySheet";
import {
  useMyTeam,
  useTeamsByStore,
} from "@coworker/app/src/core/hooks/useStoreTeams";
import {
  Country,
  useCountries,
} from "@coworker/app/src/core/hooks/useCountries";
import {
  useCountryId,
  useRoleId,
  useUID,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import FixaInlineMessage from "../../../../shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import WarningTrianglePath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/WarningTrianglePath";
import { useGetStoresByCountry } from "@coworker/app/src/core/hooks/useStoresByCountry";
import { TasksPrompt } from "./TasksPrompt";
import { useActivityData } from "../../../../activity/hooks/useActivityData";
import { DEFAULT_FILTER_VALUES } from "../../../../activity/constants/defaultFilterValues";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { Store } from "../../../../shared/types/store";
import { useUpdateUser } from "../../../../shared/hooks/users/useUpdateUser";
import { ActivityColumns } from "../../../../activity/constants/activityColumns";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { UserRoles } from "../../../../shared/enums/user/userRoles";
import FixaButton from "../../../../shared/wrappers/FixaButton/FixaButton";
import styles from "./switcher-sheet.module.css";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { SimpleControlledHalfSheet } from "../../../../shared/simple/SimpleHalfsheet/SimpleControlledHalfSheet";
import { StoreStatuses } from "@coworker/apprestructured/src/shared/enums/store/storeStatuses";
import { excludedStoreIds } from "../../../../shared/types/store";
import { SupportModal } from "@coworker/app/src/components/Profile/SupportModal";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import useUserHasRoleOnPlatformOwner from "@coworker/apprestructured/src/login/hooks/useUserHasRoleOnPlatformOwner";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useAtkomstCoreStores } from "@coworker/apprestructured/src/login/hooks/useAtkomstCoreStores";

export const StoreEnvironment = ({ trigger }: { trigger: ReactNode }) => {
  // TO-DO: COAPP-12900 - delete country data https://jira.digital.ingka.com/browse/COAPP-12900 */}
  const myCountryId = useCountryId();
  const { data: countries, isLoading: isCountriesLoading } = useCountries();
  const isChina = isChineseEnvironment();
  const myCountry = useMemo(() => {
    const china: Country = {
      isoCode: "CN",
      name: "China",
    };
    return isChina
      ? china
      : countries?.find((country) => country.isoCode === myCountryId);
  }, [isChina, countries, myCountryId]);

  const store = useMyStore();
  const myTeam = useMyTeam();
  const userRole = useRoleId();
  const [selectedCountry, setSelectedCountry] = useState(myCountry);
  const [selectedStore, setSelectedStore] = useState<Store | undefined>(store);
  const [selectedTeam, setSelectedTeam] = useState(myTeam);
  const [showTasksPrompt, setShowTasksPrompt] = useState(false);
  const [isTeamsDisabled, setIsTeamsDisabled] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const { data: stores, isLoading: isStoresLoading } = useGetStoresByCountry(
    selectedCountry?.isoCode ?? ""
  );
  const { data: teams, isLoading: isTeamsLoading } = useTeamsByStore(
    selectedStore?.storeId ?? ""
  );
  const navigate = useFixaNavigate();

  const { t } = useTranslation();
  const uid = useUID();
  const myTasks =
    useActivityData(
      { ...DEFAULT_FILTER_VALUES, assignedTo: uid },
      ActivityColumns.IN_PROGRESS
    )?.inProgressTasks ?? [];
  const myTasksCount = myTasks.data?.pages[0]?.filteredCount;

  useEffect(() => {
    setSelectedCountry(myCountry);
    setSelectedStore(store);
    setSelectedTeam(myTeam);
  }, [myCountry, store, myTeam]);

  const noValuesChanged =
    (selectedTeam?.id === myTeam?.id &&
      selectedStore?.storeId === store?.storeId &&
      selectedCountry?.isoCode === myCountry?.isoCode) ||
    showTasksPrompt;

  const onlyTeamUpdated =
    selectedTeam?.id !== myTeam?.id &&
    selectedStore?.storeId === store?.storeId &&
    selectedCountry?.isoCode === myCountry?.isoCode;

  const updateUserMutation = useUpdateUser(uid);
  const updateUser = () => {
    updateUserMutation.mutateAsync({
      uid,
      countryId: selectedCountry?.isoCode ?? "",
      storeId: selectedStore?.storeId ?? "",
      teamId: selectedTeam?.id ?? "",
    });
  };
  const [isHalfSheetOpen, setIsHalfSheetOpen] = useState(false);

  const handleOnConfirm = () => {
    if (noValuesChanged) {
      setIsHalfSheetOpen(false);
    }
    if (!noValuesChanged && selectedStore?.name && selectedTeam?.name) {
      if (myTasksCount === 0 || onlyTeamUpdated) {
        updateUser();
        trackerHelper.trackStoreSwitcher("profile");
      } else {
        setShowTasksPrompt(true);
      }
    }
  };

  const activeTeams = teams?.filter((team) => team.status === "ACTIVE");
  const {
    data: hasPlatformOwnerAuthorization,
    isLoading: isPlatformOwnerLoading,
  } = useUserHasRoleOnPlatformOwner();
  // TO-DO: COAPP-12900 - delete isGlobalSuperUser https://jira.digital.ingka.com/browse/COAPP-12900 */}
  const isGlobalSuperUser =
    (!isPlatformOwnerLoading && hasPlatformOwnerAuthorization) ||
    userRole === UserRoles.SUPERUSER;

  const filteredStoresByRole = useMemo(() => {
    if (isGlobalSuperUser) return stores;

    return stores?.filter(
      (store) =>
        store.status !== StoreStatuses.INACTIVE &&
        !excludedStoreIds.includes(store.storeId)
    );
  }, [stores, isGlobalSuperUser]);

  const enableAtkomstStoreSwitcher = useFlag(
    FLAGS.ENABLE_ATKOMST_STORE_SWITCHER
  );
  const { data: atkomstStores, isLoading: isAtkomstStoresLoading } =
    useAtkomstCoreStores(!!hasPlatformOwnerAuthorization);

  // TO-DO: COAPP-12900 - only use isAtkomstStoresLoading and delete the use memo https://jira.digital.ingka.com/browse/COAPP-12900 */}
  const loadingState = useMemo(() => {
    if (enableAtkomstStoreSwitcher) {
      return isAtkomstStoresLoading;
    } else {
      return isStoresLoading;
    }
  }, [enableAtkomstStoreSwitcher, isAtkomstStoresLoading, isStoresLoading]);

  const handleHalfSheetToggleOverride = () => {
    setIsHalfSheetOpen(!isHalfSheetOpen);
  };

  return (
    <SimpleControlledHalfSheet
      title={"storeEnvironmentString"}
      onToggle={handleHalfSheetToggleOverride}
      isOpen={isHalfSheetOpen}
      footer={
        <>
          {showTasksPrompt ? null : (
            <div className={styles["footer"] as string}>
              <FixaButton
                text={t("confirmString")}
                type="primary"
                className={styles["confirm-button"] as string}
                fluid
                loading={updateUserMutation.status === "pending"}
                onClick={handleOnConfirm}
              />
            </div>
          )}
        </>
      }
      trigger={trigger}
    >
      <>
        {/* TO-DO: COAPP-12900 - delete the country sheet https://jira.digital.ingka.com/browse/COAPP-12900 */}
        {!enableAtkomstStoreSwitcher && (
          <SwitchCountrySheet
            countries={countries}
            selectedCountry={selectedCountry!!}
            setSelectedCountry={setSelectedCountry}
            setSelectedStore={setSelectedStore}
            setSelectedTeam={setSelectedTeam}
            loading={isCountriesLoading}
            isChina={isChina}
          />
        )}
        <br />
        <SwitchStoreSheet
          stores={
            enableAtkomstStoreSwitcher ? atkomstStores : filteredStoresByRole
          }
          selectedStore={selectedStore}
          setSelectedStore={setSelectedStore}
          setSelectedTeam={setSelectedTeam}
          loading={loadingState}
        />
        <br />
        <SwitchTeamSheet
          teams={activeTeams}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          setIsTeamsDisabled={setIsTeamsDisabled}
          loading={isTeamsLoading}
          isTeamsDisabled={isTeamsDisabled}
        />
        <br />
        {!isTeamsLoading && isTeamsDisabled && selectedStore?.name && (
          <FixaInlineMessage
            title={t("noTeamsInStoreString")}
            body={
              userRole === UserRoles.COWORKER
                ? t("notPossibleWithoutTeamsString")
                : t("notPossibleWithoutTeams3String")
            }
            variant={"cautionary"}
            ssrIcon={WarningTrianglePath}
            actions={[
              userRole === UserRoles.COWORKER
                ? {
                    text: t("listOfSuperUsersString"),
                    onClick: () => {
                      setShowSupportModal(true);
                    },
                  }
                : {
                    text: t("capitalCaseTeamManagementString"),
                    onClick: () => {
                      navigate(`/teams/store/${selectedStore?.storeId}`);
                      setIsHalfSheetOpen(false);
                    },
                  },
            ]}
            className="inline-message-skapa"
          />
        )}
        <TasksPrompt
          isVisible={showTasksPrompt}
          onClose={() => setShowTasksPrompt(false)}
          myTasksCount={myTasksCount ?? 0}
          uid={uid}
          updateUser={updateUser}
        />
        <SupportModal
          modalVisibility={showSupportModal}
          onModalChange={(visibility) => setShowSupportModal(visibility)}
        />
      </>
    </SimpleControlledHalfSheet>
  );
};
